import { type RangeWidgetDescription } from 'instantsearch.js/es/connectors/range/connectRange'
import { type RefinementListWidgetDescription } from 'instantsearch.js/es/connectors/refinement-list/connectRefinementList'
import { type SortByWidgetDescription } from 'instantsearch.js/es/connectors/sort-by/connectSortBy'
import { type IndexUiState } from 'instantsearch.js/es/types/ui-state'

import { ALGOLIA_QUERY_MAX_LENGTH } from '../../algolia'

import { productSearchOptimizeSearchQueryIfItHasPzns } from './productSearchProductIdValidationRules'

/**
 * Query object from URL
 */
type RouteState = Record<string, unknown>

const getPageFromRoute = (routeState: RouteState): number => {
  const { pageNumber } = routeState

  if (typeof pageNumber !== 'string') {
    return 1
  }

  const page = Math.trunc(Number(pageNumber))

  if (Number.isNaN(page) || page < 1 || page > Number.MAX_SAFE_INTEGER) {
    return 1
  }

  return page
}

const getRangeAttributeFromRoute = (options: {
  limits: { max: number; min: number }
  rangeValues: unknown[]
}): string | undefined => {
  const { limits, rangeValues } = options

  const rangePair = rangeValues.map((rangeValue) => {
    if (typeof rangeValue !== 'string') {
      return undefined
    }

    const rangeNumber = Math.trunc(Number(rangeValue))

    if (
      Number.isNaN(rangeNumber) ||
      rangeNumber < limits.min ||
      rangeNumber > limits.max
    ) {
      return undefined
    }

    return rangeNumber
  })

  if (rangePair.every((price) => price === undefined)) {
    return undefined
  }

  if (rangePair.every((price) => price !== undefined)) {
    rangePair.sort((rangeA, rangeB) => rangeA - rangeB)
  }

  return rangePair.join(':')
}

const getPriceFromRoute = (routeState: RouteState): string | undefined =>
  getRangeAttributeFromRoute({
    limits: {
      max: 1e10,
      min: 0,
    },
    rangeValues: [routeState.priceMin, routeState.priceMax],
  })

const getQueryFromRoute = (routeState: RouteState): string | undefined => {
  const { query, q: routeStateQuery = query } = routeState

  if (typeof routeStateQuery !== 'string') {
    return undefined
  }

  return routeStateQuery
    .replaceAll(/\s+/gu, ' ')
    .trim()
    .slice(0, ALGOLIA_QUERY_MAX_LENGTH)
}

const getRatingFromRoute = (routeState: RouteState): string | undefined =>
  getRangeAttributeFromRoute({
    limits: {
      max: 5,
      min: 1,
    },
    rangeValues: [routeState.ratingMin, routeState.ratingMax],
  })
const getDiscountFromRoute = (routeState: RouteState): string | undefined => {
  const { discountInPercent } = routeState

  if (!discountInPercent || typeof discountInPercent !== 'object') {
    return undefined
  }

  return getRangeAttributeFromRoute({
    limits: {
      max: 100,
      min: 0,
    },
    rangeValues: [
      'min' in discountInPercent ? discountInPercent.min : '',
      'max' in discountInPercent ? discountInPercent.max : '',
    ],
  })
}
const getRangeFromRoute = (
  routeState: RouteState,
): RangeWidgetDescription['indexUiState']['range'] => {
  const rangeEntries = [
    ['averageRating', getRatingFromRoute(routeState)],
    ['price', getPriceFromRoute(routeState)],
    ['discountInPercent', getDiscountFromRoute(routeState)],
  ]
    // TypeScript can't infer a type after `.filter`
    .filter(([, value]) => value !== undefined) as [string, string][]

  return Object.fromEntries(rangeEntries)
}

type RefinementList =
  RefinementListWidgetDescription['indexUiState']['refinementList']

const isRefinementList = (
  refinementList: unknown,
): refinementList is RefinementList =>
  typeof refinementList === 'object' &&
  refinementList !== null &&
  Object.values(refinementList).every(
    (value) =>
      Array.isArray(value) &&
      value.every((item) => typeof item === 'string' && item),
  )

const getRefinementListFromRoute = (routeState: RouteState): RefinementList => {
  const { category } = routeState

  const refinementList: RefinementList = isRefinementList(
    routeState.refinementList,
  )
    ? routeState.refinementList
    : {}

  /**
   * Since we mapped the `refinementList.primaryCategory` under the category URL search parameter,
   * get that param and map it back to `refinementList.primaryCategory`
   * this is because Algolia wouldn't understand it without doing so.
   */
  if (typeof category === 'string' && category) {
    Object.assign(refinementList, {
      primaryCategory: [category],
    })
  }

  return refinementList
}

const getSortByFromRoute = (
  routeState: RouteState,
  sortByIndexes: string[],
): SortByWidgetDescription['indexUiState']['sortBy'] | undefined => {
  const { sortBy } = routeState

  return sortByIndexes.find((index) => index === sortBy)
}

/**
 * Transforms `routeState` into an index `ui-state` representation.
 * It receives an object that contains the index UI state stored by the router.
 * See https://www.algolia.com/doc/api-reference/widgets/instantsearch/react/#widget-param-routing.
 */
export const productSearchAlgoliaRouteToIndexUiState = (
  routeState: RouteState,
  options: { sortByIndexes: string[] },
): IndexUiState => {
  const { sortByIndexes } = options
  const queryFromRoute = getQueryFromRoute(routeState)
  const { searchQuery } =
    productSearchOptimizeSearchQueryIfItHasPzns(queryFromRoute)

  return {
    page: getPageFromRoute(routeState),
    query: searchQuery,
    range: getRangeFromRoute(routeState),
    refinementList: getRefinementListFromRoute(routeState),
    sortBy: getSortByFromRoute(routeState, sortByIndexes),
  }
}
